import type { ReactNode } from "react";
import { Card, SegmentedMeter } from "@bfc-software/pallet";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import type { TFunction } from "i18next";

import { formatCount, formatDuration, formatPercent } from "../-data/format";

import type { DashRoute } from "./routes-table";
import styles from "./route-drawer.module.css";

type Props = {
  row: DashRoute;
  lang: string;
  t: TFunction;
};

function Field({
  label,
  testRole,
  children,
}: {
  label: string;
  testRole: string;
  children: ReactNode;
}) {
  return (
    <div className={styles.field} data-test-role={testRole}>
      <dt className={styles.fieldLabel}>{label}</dt>
      <dd className={styles.fieldValue}>{children}</dd>
    </div>
  );
}

/**
 * The drawer's first tab: the route on one screen -- its scan quality, who ran
 * it and on what device, and the totals. It reads the route detail the drawer
 * already loaded for its header, so it costs no request of its own.
 */
export function SummaryTab({ row, lang, t }: Props) {
  const none = t("none");
  const n = (value: number) => formatCount(value, lang);
  const pct = formatPercent(row.scanPct, lang) ?? none;

  return (
    <div className={styles.tab} data-test-role="drawer-summary">
      <Card
        variant="panel"
        layout="body-only"
        body={
          <>
            <div className={styles.meterHead}>
              <span className={styles.primary}>
                {t("drawer.summary.scanQuality")}
              </span>
              <span className={styles.meterHeadline}>{pct}</span>
            </div>
            <SegmentedMeter
              aria-label={`${t("drawer.summary.scanQuality")}: ${pct}`}
              segments={[
                {
                  value: row.piecesScanned,
                  tone: "good",
                  label: t("drawer.summary.scanned", {
                    count: row.piecesScanned,
                  }),
                },
                {
                  value: row.piecesNonScanned,
                  tone: "fair",
                  label: t("drawer.summary.nonScanned", {
                    count: row.piecesNonScanned,
                  }),
                },
              ]}
            />
          </>
        }
      />

      <dl className={styles.fields}>
        <Field
          label={t("drawer.summary.warehouse")}
          testRole="summary-warehouse"
        >
          {row.warehouse}
        </Field>
        <Field label={t("drawer.summary.driver")} testRole="summary-driver">
          {row.driverName || row.driver || none}
        </Field>
        <Field label={t("drawer.summary.truck")} testRole="summary-truck">
          {row.truck || none}
        </Field>
        <Field label={t("drawer.summary.serial")} testRole="summary-serial">
          {row.serial || none}
        </Field>
        <Field
          label={t("drawer.summary.appVersion")}
          testRole="summary-app-version"
        >
          {row.appVersion || none}
        </Field>
        <Field label={t("drawer.summary.started")} testRole="summary-started">
          {row.startTimestamp ?? none}
        </Field>
        <Field label={t("drawer.summary.finished")} testRole="summary-finished">
          {row.finishTimestamp ?? none}
        </Field>
        <Field label={t("drawer.summary.stops")} testRole="summary-stops">
          {n(row.stopsDelivered)} / {n(row.stops)}
        </Field>
        <Field
          label={t("drawer.summary.delivered")}
          testRole="summary-delivered"
        >
          {n(row.piecesDelivered)}
        </Field>
        <Field
          label={t("drawer.summary.scannedLabel")}
          testRole="summary-scanned"
        >
          {n(row.piecesScanned)}
        </Field>
        <Field
          label={t("drawer.summary.nonScannedLabel")}
          testRole="summary-non-scanned"
        >
          {n(row.piecesNonScanned)}
        </Field>
        <Field label={t("drawer.summary.scanPct")} testRole="summary-scan-pct">
          {pct}
        </Field>
        <Field label={t("drawer.summary.returns")} testRole="summary-returns">
          {n(row.currentReturns)}
        </Field>
        <Field
          label={t("drawer.summary.completedPieces")}
          testRole="summary-completed-pieces"
        >
          {n(row.completedPieces)}
        </Field>
        <Field label={t("drawer.summary.pph")} testRole="summary-pph">
          {n(row.pph)}
        </Field>
        <Field
          label={t("drawer.summary.scanExceptions")}
          testRole="summary-scan-exceptions"
        >
          {n(row.exceptionCount)}
        </Field>
        <Field
          label={t("drawer.summary.routeTime")}
          testRole="summary-route-time"
        >
          {formatDuration(row.routeSeconds)}
        </Field>
        <Field
          label={t("drawer.summary.stopTime")}
          testRole="summary-stop-time"
        >
          {formatDuration(row.stopSeconds)}
        </Field>
      </dl>
    </div>
  );
}
